import React, { useMemo, useState } from 'react';
import { Container, Card, Button, Form, Modal, Spinner, Alert, Badge, Table, Tabs, Tab } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  listToolLocations,
  createToolLocation,
  updateToolLocation
} from '../../services/toolLocationService';
import { listProjects } from '../../services/projectService';
import LocationPicker from '../../components/locations/LocationPicker';
import DashboardLayout from '../../components/layout/DashboardLayout';

const TYPE_OPTIONS = [
  { value: 'zona', label: 'Zona' },
  { value: 'bodega', label: 'Bodega' },
  { value: 'vehiculo', label: 'Vehículo' },
  { value: 'proyecto', label: 'Proyecto' },
  { value: 'sub', label: 'Sub-ubicación' },
  { value: 'otro', label: 'Otra' }
];

const TYPE_BADGE = {
  zona: 'primary',
  bodega: 'info',
  vehiculo: 'warning',
  proyecto: 'success',
  sub: 'secondary',
  otro: 'dark'
};

const LocationsPage = () => {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('globals');

  const { data: locationsData, isLoading, error } = useQuery({
    queryKey: ['tool-locations', 'admin-all'],
    queryFn: () => listToolLocations({ active_only: false }),
    staleTime: 60 * 1000,
    enabled: isAdmin
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-locations'],
    queryFn: () => listProjects({ pageSize: 200 }),
    staleTime: 5 * 60 * 1000,
    enabled: isAdmin
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tool-locations'] });
  };

  const createMutation = useMutation({
    mutationFn: createToolLocation,
    onSuccess: ({ message, error: err }) => {
      if (err) { toast.error(err.message); return; }
      toast.success(message || 'Ubicación creada.');
      invalidate();
      setShowCreate(false);
    },
    onError: (e) => toast.error(e.message)
  });

  const updateMutation = useMutation({
    mutationFn: updateToolLocation,
    onSuccess: ({ message, error: err }) => {
      if (err) { toast.error(err.message); return; }
      toast.success(message || 'Ubicación actualizada.');
      invalidate();
      setEditing(null);
    },
    onError: (e) => toast.error(e.message)
  });

  const locations = useMemo(() => locationsData?.data || [], [locationsData]);
  const projects = useMemo(() => projectsData?.data || [], [projectsData]);
  const projectMap = useMemo(() => {
    const m = new Map();
    projects.forEach((p) => m.set(p.id, p));
    return m;
  }, [projects]);
  const locationMap = useMemo(() => {
    const m = new Map();
    locations.forEach((l) => m.set(l.id, l));
    return m;
  }, [locations]);

  const tabs = useMemo(() => {
    const globals = locations.filter((l) => l.is_active && !l.project_id);
    const byProject = locations.filter((l) => l.is_active && !!l.project_id);
    const inactive = locations.filter((l) => !l.is_active);
    return { globals, byProject, inactive };
  }, [locations]);

  if (!isAdmin) {
    return <Alert variant="danger" className="m-3">Solo administradores pueden gestionar ubicaciones.</Alert>;
  }

  const renderTable = (rows, { showProject = false, muted = false } = {}) => (
    <Table responsive hover className="mb-0">
      <thead>
        <tr>
          <th>Código</th>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Padre</th>
          {showProject && <th>Proyecto</th>}
          <th>Estado</th>
          <th style={{ width: 200 }}></th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={showProject ? 7 : 6} className="text-center text-muted py-4">
              Sin ubicaciones
            </td>
          </tr>
        )}
        {rows.map((l) => (
          <tr key={l.id} className={muted ? 'text-muted' : ''}>
            <td><code className="small">{l.code}</code></td>
            <td><strong>{l.label}</strong></td>
            <td><Badge bg={TYPE_BADGE[l.type] || 'secondary'}>{l.type}</Badge></td>
            <td>{l.parent_location_id ? (locationMap.get(l.parent_location_id)?.label || '—') : <span className="text-muted">—</span>}</td>
            {showProject && (
              <td>{l.project_id ? (projectMap.get(l.project_id)?.project_number || projectMap.get(l.project_id)?.title || '—') : <span className="text-muted">—</span>}</td>
            )}
            <td>
              {l.is_active
                ? <Badge bg="success">Activa</Badge>
                : <Badge bg="secondary">Inactiva</Badge>}
            </td>
            <td>
              <div className="d-flex gap-1 justify-content-end">
                <Button size="sm" variant="outline-primary" onClick={() => setEditing(l)}>
                  <i className="bi bi-pencil" />
                </Button>
                <Button
                  size="sm"
                  variant={l.is_active ? 'outline-secondary' : 'outline-success'}
                  onClick={() => updateMutation.mutate({ location_id: l.id, is_active: !l.is_active })}
                  disabled={updateMutation.isPending}
                >
                  {l.is_active ? 'Desactivar' : 'Reactivar'}
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <DashboardLayout>
    <Container fluid className="py-3 px-3" style={{ maxWidth: 1100 }}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button as={Link} to="/herramientas" variant="link" className="p-0" aria-label="Volver">
            <i className="bi bi-arrow-left fs-4" />
          </Button>
          <h4 className="mb-0">
            <i className="bi bi-geo-alt me-2" />
            Ubicaciones de herramientas
          </h4>
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          <i className="bi bi-plus-lg me-1" />Nueva ubicación
        </Button>
      </div>

      <p className="text-muted small">
        Catálogo curado donde viven las herramientas. Las ubicaciones tipo
        <strong> proyecto</strong> se atan a un proyecto específico; <strong>sub</strong> debe colgar de otra
        ubicación. Podés desactivar sin perder los datos asociados.
      </p>

      {isLoading && <div className="text-center py-4"><Spinner animation="border" /></div>}
      {error && <Alert variant="danger">{error.message}</Alert>}

      {!isLoading && (
        <Card className="modern-card">
          <Card.Body className="p-0">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="px-3 pt-2"
            >
              <Tab eventKey="globals" title={`Globales (${tabs.globals.length})`}>
                <div className="p-2">{renderTable(tabs.globals)}</div>
              </Tab>
              <Tab eventKey="projects" title={`Por proyecto (${tabs.byProject.length})`}>
                <div className="p-2">{renderTable(tabs.byProject, { showProject: true })}</div>
              </Tab>
              <Tab eventKey="inactive" title={`Inactivas (${tabs.inactive.length})`}>
                <div className="p-2">{renderTable(tabs.inactive, { showProject: true, muted: true })}</div>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      )}

      <LocationFormModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        onSubmit={(payload) => createMutation.mutate(payload)}
        pending={createMutation.isPending}
        title="Nueva ubicación"
        projects={projects}
      />

      <LocationFormModal
        show={!!editing}
        onHide={() => setEditing(null)}
        location={editing}
        onSubmit={(payload) => updateMutation.mutate({ location_id: editing.id, label: payload.label })}
        pending={updateMutation.isPending}
        title="Editar ubicación"
        projects={projects}
      />
    </Container>
    </DashboardLayout>
  );
};

const LocationFormModal = ({ show, onHide, location, onSubmit, pending, title, projects }) => {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState('zona');
  const [projectId, setProjectId] = useState('');
  const [parentId, setParentId] = useState('');

  React.useEffect(() => {
    if (show) {
      setCode(location?.code || '');
      setLabel(location?.label || '');
      setType(location?.type || 'zona');
      setProjectId(location?.project_id || '');
      setParentId(location?.parent_location_id || '');
    }
  }, [show, location]);

  const isEditing = !!location;
  const requiresProject = type === 'proyecto';
  const requiresParent = type === 'sub';

  const canSubmit =
    !!label.trim() &&
    (isEditing || !!code.trim()) &&
    (isEditing || !requiresProject || !!projectId) &&
    (isEditing || !requiresParent || !!parentId);

  const handle = () => {
    if (isEditing) {
      onSubmit({ label: label.trim() });
      return;
    }
    const payload = {
      code: code.trim(),
      label: label.trim(),
      type
    };
    if (projectId) payload.project_id = projectId;
    if (parentId) payload.parent_location_id = parentId;
    onSubmit(payload);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isEditing && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Código <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="bodega-cartago"
                disabled={pending}
              />
              <Form.Text className="text-muted">
                Identificador único. Se normaliza en el servidor (minúsculas, sin acentos).
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tipo <span className="text-danger">*</span></Form.Label>
              <Form.Select value={type} onChange={(e) => setType(e.target.value)} disabled={pending}>
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {requiresProject && (
              <Form.Group className="mb-3">
                <Form.Label>Proyecto <span className="text-danger">*</span></Form.Label>
                <Form.Select value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={pending}>
                  <option value="">-- Seleccionar --</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.project_number || ''} {p.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>
                Padre {requiresParent && <span className="text-danger">*</span>}
                {!requiresParent && <small className="text-muted"> (opcional)</small>}
              </Form.Label>
              <LocationPicker
                value={parentId}
                onChange={setParentId}
                disabled={pending}
                placeholder="-- Sin padre --"
                showCount={false}
              />
            </Form.Group>
          </>
        )}
        {isEditing && (
          <Alert variant="light" className="py-2 small">
            <div><strong>Código:</strong> <code>{code}</code> <span className="ms-2 text-muted">(inmutable)</span></div>
            <div><strong>Tipo:</strong> <code>{type}</code> <span className="ms-2 text-muted">(inmutable)</span></div>
          </Alert>
        )}
        <Form.Group className="mb-3">
          <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
          <Form.Control
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Bodega Cartago"
            disabled={pending}
            autoFocus={isEditing}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={pending}>Cancelar</Button>
        <Button variant="primary" disabled={!canSubmit || pending} onClick={handle}>
          {pending && <Spinner size="sm" animation="border" className="me-1" />}
          {isEditing ? 'Guardar cambios' : 'Crear ubicación'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LocationsPage;
